// Dispatcher for Monte-Carlo runs.
//
// Below the threshold we just run on the main thread — spawning a
// worker would cost more than the simulation itself. Above the
// threshold we ship the job to a dedicated worker so the UI stays
// responsive at 50k–500k samples.
//
// Falls back to main-thread execution gracefully if Worker isn't
// available (older browsers, server-side test runners, etc.).

import { calculateMonteCarlo } from './monteCarlo';

// Below this sample count the worker round-trip outweighs the gain.
export const WORKER_THRESHOLD = 5000;

let workerInstance = null;
let pendingRequests = new Map();
let nextId = 1;

function getWorker() {
  if (workerInstance) return workerInstance;
  if (typeof Worker === 'undefined') return null;
  try {
    // Vite recognizes this import.meta.url pattern and bundles the
    // worker as a separate chunk. The `type: 'module'` is mandatory
    // because monteCarlo.js uses ES module imports.
    workerInstance = new Worker(
      new URL('../workers/monteCarlo.worker.js', import.meta.url),
      { type: 'module' }
    );
    workerInstance.onmessage = (e) => {
      const { id, result, error } = e.data || {};
      const pending = pendingRequests.get(id);
      if (!pending) return;
      pendingRequests.delete(id);
      if (error) pending.reject(new Error(error));
      else pending.resolve(result);
    };
    workerInstance.onerror = (err) => {
      // Reject all pending requests so the UI doesn't hang.
      for (const pending of pendingRequests.values()) {
        pending.reject(err);
      }
      pendingRequests.clear();
      workerInstance = null;  // force re-create on next call
    };
  } catch {
    workerInstance = null;
  }
  return workerInstance;
}

/**
 * Runs Monte Carlo, returning a Promise that resolves with the result.
 *
 * @param {Array} elements
 * @param {object} options - same as calculateMonteCarlo (no `random` fn)
 * @returns {Promise<object>}
 */
export function runMonteCarlo(elements, options = {}) {
  const samples = options.samples || 10000;

  // Sub-threshold or no worker support → main thread.
  const worker = samples >= WORKER_THRESHOLD ? getWorker() : null;
  if (!worker) {
    return Promise.resolve(calculateMonteCarlo(elements, options));
  }

  return new Promise((resolve, reject) => {
    const id = nextId++;
    pendingRequests.set(id, { resolve, reject });
    // We don't transfer Float64Arrays etc. — the result is a tiny POJO
    // compared to per-element samplers, so postMessage's structured-clone
    // copy cost is negligible.
    worker.postMessage({ id, elements, options });
  });
}

/**
 * Tear down the worker (useful for tests / hot-reload).
 */
export function terminateMcWorker() {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
  }
  pendingRequests.clear();
}
